import { FormField } from '@/components/ui/form';
import useSymbol from '@/hooks/use-symbol';
import { useCurrentAccount } from '@/store/use-account-store';
import { useMarketStore } from '@/store/use-markets-store';
import { MarketStatus, OrderType } from '@/types';
import { cn } from '@/utils/cn';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useStore } from 'zustand';
import { OrderCheckbox } from '../order-checkbox';
import { OrderFormSchema } from '../order-form';
import { FormItem, FormLabel } from '../order-form-components';

type Props = {
  onChange?: (checked: boolean) => void;
};

export default function ReduceOnly({ onChange }: Props) {
  const symbol = useSymbol();
  const { t } = useTranslation();

  const form = useFormContext<OrderFormSchema>();

  const orderType = form.watch('order.orderType');
  const reduceOnly = form.watch('order.reduceOnly');

  const hasPosition = useCurrentAccount((state) => symbol in state.positions);

  const isReduceOnly = useStore(
    useMarketStore(symbol),
    (state) => state.marketSpec.status === MarketStatus.REDUCE_ONLY,
  );

  useEffect(() => {
    if (isReduceOnly) {
      form.setValue('order.reduceOnly', true);
    } else if (reduceOnly && !hasPosition) {
      form.setValue('order.reduceOnly', false);
    }
  }, [hasPosition, reduceOnly, isReduceOnly]);

  return (
    <FormField
      control={form.control}
      name="order.reduceOnly"
      render={({ field }) => (
        <FormItem
          className={cn(
            'group justify-start',
            (orderType === OrderType.LIMIT || orderType === OrderType.SCALE) &&
              'w-max',
          )}
        >
          <OrderCheckbox
            id="reduce-only"
            className="peer"
            disabled={!hasPosition || isReduceOnly}
            checked={field.value}
            onCheckedChange={(checked) => {
              onChange?.(checked === true);
              if (checked === true) {
                form.setValue('tp.limitPrice', '');
                form.setValue('sl.limitPrice', '');
              }
              field.onChange(checked === true);
            }}
          />
          <FormLabel
            className="cursor-pointer select-none pl-3 transition-colors group-hover:text-foreground peer-disabled:opacity-60 group-hover:peer-disabled:text-vestgrey-100"
            htmlFor="reduce-only"
          >
            {t('orderForm.reduceOnly')}
          </FormLabel>
        </FormItem>
      )}
    />
  );
}
